// hooks/use-help-desk.ts
import { useState, useEffect } from "react";
import { IDriverTypeResponse, IUserTypeResponse } from '../../../utils/types';
import { useAppDispatch, useAppSelector } from '../../../Redux/store';
import { useGetAllComplaints } from '../../../apis/help-desks/get-all-complaints';
import { setHelpDeskCount } from '../../../Redux/slices/ui';
import socket from '../../../lib/socket-io';

type ApiResponse = IUserTypeResponse | IDriverTypeResponse;

const useHelpDesk = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [userType, setUserType] = useState("");
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState({ search: "", userType: "" });
    const [selectedComplaint, setSelectedComplaint] = useState<ApiResponse | null>(null);
    const [isChatVisible, setIsChatVisible] = useState(false);
    const [isDetailsVisible, setIsDetailsVisible] = useState(false);

    const dispatch = useAppDispatch();
    const helpDeskCount = useAppSelector((state) => state.ui.helpDeskUnreadCount);

    const { data: complaintsData, isFetching, refetch } = useGetAllComplaints({
        limit: 10,
        page: currentPage,
        userType: filters.userType,
        search: filters.search,
    });

    const [data, setData] = useState<ApiResponse[]>([]);
    const totalPages = complaintsData?.totalPages ?? 0;

    useEffect(() => {
        if (complaintsData?.docs) {
            setData(complaintsData.docs);
        }
    }, [complaintsData]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentPage(1);
        setFilters({ search, userType });
    };

    const handleClear = () => {
        setCurrentPage(1);
        setUserType("");
        setSearch("");
        setFilters({ search: "", userType: "" });
    };

    const handleOpenChat = (complaint: ApiResponse) => {
        setSelectedComplaint(complaint);
        setIsChatVisible(true);
    };

    const handleOpenDetails = (complaint: ApiResponse) => {
        setSelectedComplaint(complaint);
        setIsDetailsVisible(true);
    };

    const handleCloseChat = () => {
        setSelectedComplaint(null);
        setIsChatVisible(false);
    };

    const handleCloseDetails = () => {
        setSelectedComplaint(null);
        setIsDetailsVisible(false);
    };

    const onMessageRead = (complaintId: string) => {
        setData((prev) => {
            const index = prev.findIndex((c) => c._id === complaintId);
            if (index === -1) return prev;
            dispatch(setHelpDeskCount(helpDeskCount - prev[index].unreadCount));
            const updated = [...prev];
            updated[index] = { ...updated[index], unreadCount: 0 };
            return updated;
        });
    };

    // ─── Socket Listeners ──────────────────────────────────────────────────────
    useEffect(() => {
        const handleSocketMessage = (socketData: {
            data: { complaint: string; unreadCount: number };
        }) => {
            setData((prev) => {
                const index = prev.findIndex((c) => c._id === socketData?.data?.complaint);
                if (index === -1) {
                    refetch();
                    return prev;
                }

                // Update the unread count for the complaint that received the message
                const updated = [...prev];
                updated[index] = {
                    ...updated[index],
                    unreadCount: socketData.data.unreadCount
                };

                // ──────────────────────────────────────────────────────────────
                // Sort by unread count (highest first) - moves new messages to top
                // ──────────────────────────────────────────────────────────────
                const sorted = updated.sort((a, b) => {
                    const aUnread = a.unreadCount || 0;
                    const bUnread = b.unreadCount || 0;

                    // Sort by unread count descending (highest first)
                    if (bUnread !== aUnread) {
                        return bUnread - aUnread;
                    }

                    // If unread counts are equal, sort by updatedAt (most recent first)
                    const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
                    const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
                    return bTime - aTime;
                });

                return sorted;
            });
        };

        const handleNewComplaint = () => refetch();

        socket.on("receive_unread_count", handleSocketMessage);
        socket.on("receive_complaint", handleNewComplaint);

        return () => {
            socket.off("receive_unread_count", handleSocketMessage);
            socket.off("receive_complaint", handleNewComplaint);
        };
    }, []);

    return {
        data,
        totalPages,
        isFetching,
        currentPage,
        setCurrentPage,
        userType,
        setUserType,
        search,
        setSearch,
        selectedComplaint,
        isChatVisible,
        isDetailsVisible,
        handleSearch,
        handleClear,
        handleOpenChat,
        handleOpenDetails,
        handleCloseChat,
        handleCloseDetails,
        onMessageRead,
    };
};

export default useHelpDesk;